import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, DollarSignIcon, PlusIcon } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { JobCost } from '../types';
interface AddCostsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (costs: Omit<JobCost, 'id' | 'date'>[]) => void;
  jobTitle: string;
}
const costCategories: {
  value: JobCost['category'];
  label: string;
  icon: string;
}[] = [{
  value: 'fuel',
  label: 'Fuel',
  icon: '⛽'
}, {
  value: 'labor',
  label: 'Labor',
  icon: '👷'
}, {
  value: 'parts',
  label: 'Parts',
  icon: '🔧'
}, {
  value: 'media',
  label: 'Media',
  icon: '💎'
}, {
  value: 'miscellaneous',
  label: 'Miscellaneous',
  icon: '📦'
}];
export function AddCostsModal({
  isOpen,
  onClose,
  onSave,
  jobTitle
}: AddCostsModalProps) {
  const [costs, setCosts] = useState<Record<JobCost['category'], string>>({
    fuel: '',
    labor: '',
    parts: '',
    media: '',
    miscellaneous: ''
  });
  const [descriptions, setDescriptions] = useState<Record<JobCost['category'], string>>({
    fuel: '',
    labor: '',
    parts: '',
    media: '',
    miscellaneous: ''
  });
  const handleSave = () => {
    const costEntries: Omit<JobCost, 'id' | 'date'>[] = [];
    (Object.keys(costs) as JobCost['category'][]).forEach(category => {
      const amount = parseFloat(costs[category]);
      if (amount > 0) {
        costEntries.push({
          category,
          amount,
          description: descriptions[category] || `${costCategories.find(c => c.value === category)?.label} cost`
        });
      }
    });
    if (costEntries.length > 0) {
      onSave(costEntries);
      handleClose();
    }
  };
  const handleClose = () => {
    setCosts({
      fuel: '',
      labor: '',
      parts: '',
      media: '',
      miscellaneous: ''
    });
    setDescriptions({
      fuel: '',
      labor: '',
      parts: '',
      media: '',
      miscellaneous: ''
    });
    onClose();
  };
  const totalCost = Object.values(costs).reduce((sum, val) => {
    const num = parseFloat(val);
    return sum + (isNaN(num) ? 0 : num);
  }, 0);
  return <AnimatePresence data-id="element-22">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" data-id="element-23" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-id="element-24">
            <motion.div initial={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-id="element-25">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 flex items-center justify-between" data-id="element-26">
                <div className="flex-1" data-id="element-27">
                  <h2 className="text-xl font-bold mb-1" data-id="element-28">Add Costs</h2>
                  <p className="text-slate-300 text-sm truncate" data-id="element-29">{jobTitle}</p>
                </div>
                <motion.button whileTap={{
              scale: 0.9
            }} onClick={handleClose} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-30">
                  <XIcon className="w-6 h-6" data-id="element-31" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4" data-id="element-32">
                {costCategories.map((category, index) => <motion.div key={category.value} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} className="space-y-2" data-id="element-33">
                    <div className="flex items-center gap-2 mb-2" data-id="element-34">
                      <span className="text-2xl" data-id="element-35">{category.icon}</span>
                      <label className="text-sm font-bold text-slate-900" data-id="element-36">
                        {category.label}
                      </label>
                    </div>
                    <Input type="number" placeholder="0.00" value={costs[category.value]} onChange={e => setCosts({
                ...costs,
                [category.value]: e.target.value
              })} icon={<DollarSignIcon className="w-5 h-5" data-id="element-38" />} step="0.01" min="0" data-id="element-37" />
                    <Input placeholder={`Description (optional)`} value={descriptions[category.value]} onChange={e => setDescriptions({
                ...descriptions,
                [category.value]: e.target.value
              })} className="text-sm" data-id="element-39" />
                  </motion.div>)}
              </div>

              {/* Footer */}
              <div className="border-t-2 border-slate-100 p-6 bg-slate-50" data-id="element-40">
                <div className="flex items-center justify-between mb-4" data-id="element-41">
                  <span className="text-sm font-semibold text-slate-600" data-id="element-42">
                    Total Cost
                  </span>
                  <motion.span key={totalCost} initial={{
                scale: 1.2,
                color: '#ea580c'
              }} animate={{
                scale: 1,
                color: '#0f172a'
              }} className="text-2xl font-bold text-slate-900" data-id="element-43">
                    ${totalCost.toFixed(2)}
                  </motion.span>
                </div>
                <div className="flex gap-3" data-id="element-44">
                  <motion.div whileTap={{
                scale: 0.98
              }} className="flex-1" data-id="element-45">
                    <Button variant="secondary" fullWidth onClick={handleClose} data-id="element-46">
                      Cancel
                    </Button>
                  </motion.div>
                  <motion.div whileTap={{
                scale: 0.98
              }} className="flex-1" data-id="element-47">
                    <Button variant="primary" fullWidth onClick={handleSave} disabled={totalCost === 0} className="flex items-center justify-center gap-2" data-id="element-48">
                      <PlusIcon className="w-4 h-4" data-id="element-49" />
                      Add Costs
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}